/**
 * pixelFilters.js — fast in-place colour filters for canvas ImageData.
 *
 * Why this file exists: `CanvasRenderingContext2D.filter` is NOT supported
 * on iOS Safari / WebKit. The previous implementation relied on `ctx.filter`
 * which silently no-ops on iPhone, so the colour filter was visible in the
 * <video> preview (CSS-side, supported) but never made it into the saved
 * photo or recorded video. This module re-implements every preset as a
 * direct pixel-level transform, which works identically on every browser.
 *
 * All maths are matrix-on-RGB so we never touch HSL conversion (slow).
 * Cost ~6-10 ms on a 720x1280 frame on mid-range phones — fast enough for
 * 30 fps video.
 */

// Each preset is a tiny "recipe" object the inner loop reads — keeping the
// per-pixel branching minimal. `mode` triggers the sepia / grayscale matrix.
const PRESETS = {
  none:      null,
  noir:      { mode: 'gray',  contrast: 1.1 },
  sepia:     { mode: 'sepia', strength: 0.8,  contrast: 1.05, brightness: 1.05 },
  vintage:   { mode: 'sepia', strength: 0.35, contrast: 1.1,  sat: 1.2, hue: -10 },
  vivid:     {                                 contrast: 1.15, sat: 1.8 },
  cool:      {                                                 hue: -25, sat: 1.1, brightness: 1.05 },
  warm:      {                                                 hue:  15, sat: 1.2, brightness: 1.05 },
  faded:     {                                 contrast: 0.9,  sat: 0.7, brightness: 1.1 },
  drama:     {                                 contrast: 1.4,  sat: 1.1, brightness: 0.95 },
  neon:      {                                 contrast: 1.2,  sat: 2.5, hue: 45 },
  hyperreal: {                                 contrast: 1.25, sat: 1.5, brightness: 1.08 },
};

const LUMA_R = 0.213, LUMA_G = 0.715, LUMA_B = 0.072;

// Sepia matrix (the classic web-friendly one used by Photoshop / CSS).
const SEP_R = [0.393, 0.769, 0.189];
const SEP_G = [0.349, 0.686, 0.168];
const SEP_B = [0.272, 0.534, 0.131];

/**
 * applyPixelFilter — mutates `data` (ImageData.data) in place.
 * @param {Uint8ClampedArray} data
 * @param {string} filterId — must be one of the FILTERS ids.
 */
export function applyPixelFilter(data, filterId) {
  const p = PRESETS[filterId];
  if (!p) return; // 'none' or unknown

  const mode       = p.mode || null;
  const strength   = p.strength   != null ? p.strength   : 1;
  const contrast   = p.contrast   != null ? p.contrast   : 1;
  const brightness = p.brightness != null ? p.brightness : 1;
  const sat        = p.sat        != null ? p.sat        : 1;
  const hue        = p.hue        != null ? p.hue        : 0;

  // Pre-compute a 3x3 hue-rotation matrix in RGB space.
  // Derived from https://beesbuzz.biz/code/16-hsv-color-transforms (closed form).
  const hueRad = (hue * Math.PI) / 180;
  const cosH = Math.cos(hueRad);
  const sinH = Math.sin(hueRad);
  const hM00 = 0.213 + 0.787 * cosH - 0.213 * sinH;
  const hM01 = 0.715 - 0.715 * cosH - 0.715 * sinH;
  const hM02 = 0.072 - 0.072 * cosH + 0.928 * sinH;
  const hM10 = 0.213 - 0.213 * cosH + 0.143 * sinH;
  const hM11 = 0.715 + 0.285 * cosH + 0.140 * sinH;
  const hM12 = 0.072 - 0.072 * cosH - 0.283 * sinH;
  const hM20 = 0.213 - 0.213 * cosH - 0.787 * sinH;
  const hM21 = 0.715 - 0.715 * cosH + 0.715 * sinH;
  const hM22 = 0.072 + 0.928 * cosH + 0.072 * sinH;
  const doHue = hue !== 0;

  // Pre-compute contrast shift (around midpoint 128).
  const cM = contrast;
  const cB = 128 * (1 - cM);
  const doContrast = contrast !== 1;
  const doBrightness = brightness !== 1;
  const doSat = sat !== 1;

  const len = data.length;
  for (let i = 0; i < len; i += 4) {
    let r = data[i], g = data[i + 1], b = data[i + 2];

    // 1) Saturation (linear blend with luminance — fast and looks great).
    if (doSat) {
      const gray = LUMA_R * r + LUMA_G * g + LUMA_B * b;
      r = gray + (r - gray) * sat;
      g = gray + (g - gray) * sat;
      b = gray + (b - gray) * sat;
    }

    // 2) Hue rotation matrix.
    if (doHue) {
      const nR = r * hM00 + g * hM01 + b * hM02;
      const nG = r * hM10 + g * hM11 + b * hM12;
      const nB = r * hM20 + g * hM21 + b * hM22;
      r = nR; g = nG; b = nB;
    }

    // 3) Sepia / grayscale matrix.
    if (mode === 'sepia') {
      const sR = SEP_R[0] * r + SEP_R[1] * g + SEP_R[2] * b;
      const sG = SEP_G[0] * r + SEP_G[1] * g + SEP_G[2] * b;
      const sB = SEP_B[0] * r + SEP_B[1] * g + SEP_B[2] * b;
      r = r + (sR - r) * strength;
      g = g + (sG - g) * strength;
      b = b + (sB - b) * strength;
    } else if (mode === 'gray') {
      const gray = LUMA_R * r + LUMA_G * g + LUMA_B * b;
      r = g = b = gray;
    }

    // 4) Brightness (simple scale).
    if (doBrightness) {
      r *= brightness; g *= brightness; b *= brightness;
    }

    // 5) Contrast (linear around 128).
    if (doContrast) {
      r = cM * r + cB;
      g = cM * g + cB;
      b = cM * b + cB;
    }

    // Manual clamp (faster than Math.max/min on hot loops).
    data[i]     = r < 0 ? 0 : r > 255 ? 255 : r;
    data[i + 1] = g < 0 ? 0 : g > 255 ? 255 : g;
    data[i + 2] = b < 0 ? 0 : b > 255 ? 255 : b;
  }
}
